import styled from "@emotion/styled";
import {
  useCallback,
  useState,
  type ChangeEventHandler,
  type FocusEventHandler,
  type HTMLAttributes,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type ReactNode,
} from "react";

import { ellipsise, FONT_WEIGHT_NORMAL } from "@skilitics/designer-ui-components";

const LayerTitleContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 100%;
  min-width: 0;
  .fa-icon-wrapper {
    color: ${({ theme }) => theme.generalTextIconVariant};
  }
`;

const LayerName = styled.div`
  height: 15px;
  width: 100%;

  line-height: 15px;
  font-size: ${({ theme }) => theme.fontSizeSmall};
  font-weight: ${FONT_WEIGHT_NORMAL};

  user-select: none;

  margin: 0;
  padding: 0;

  ${ellipsise};
  min-width: 0;
`;

const LayerNameInput = styled.input`
  height: 15px;
  line-height: 15px;
  font-size: ${({ theme }) => theme.fontSizeSmall};
  font-weight: ${FONT_WEIGHT_NORMAL};
  color: inherit;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
`;

type Props = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  icon?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  title: string;
  onTitleChange?: (newTitle: string) => void;
};

export const LayerTitle = ({ title, icon, onClick, selected, disabled, onTitleChange }: Props) => {
  const [editing, setEditing] = useState(false);
  const [editingText, setEditingText] = useState("");

  const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (selected && !editing) {
        setEditing(true);
        setEditingText(title);
      } else {
        onClick?.(e);
      }
    },
    [editing, onClick, selected, title],
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => setEditingText(e.target.value);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter" || e.key === "Escape") {
      e.currentTarget.blur();
    }
  };

  const handleFocus: FocusEventHandler<HTMLInputElement> = (e) => {
    e.currentTarget.select();
  };

  const handleBlur = () => {
    onTitleChange?.(editingText);
    setEditing(false);
  };

  return (
    <LayerTitleContainer>
      {icon}
      {editing && !disabled ? (
        <LayerNameInput
          autoFocus
          onFocus={handleFocus}
          value={editingText}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          maxLength={100}
        />
      ) : (
        <LayerName onClick={handleClick}>{title}</LayerName>
      )}
    </LayerTitleContainer>
  );
};
